/**
 * Pure async API calls — no React dependencies.
 */
import notify from "devextreme/ui/notify";
import EncryptionUtil from "../../../utils/encryptionUtil.js";
import { groupBy3 } from "../../../utils/objectUtils.js";

const { REACT_APP_SERVER_URL } = process.env;

/**
 * Saves draft/template changes to the server.
 * @param {object} payload - The draft payload from buildDraftPayload
 * @returns {Promise<{ok: boolean, id?: string, status?: string}>}
 */
export async function saveChangesToServer(payload) {
  const jsonString = JSON.stringify(payload);

  try {
    const response = await fetch(`${REACT_APP_SERVER_URL}SaveChanges`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: jsonString,
      credentials: "include",
    });

    if (response.ok) {
      const responseData = await response.json();
      if (responseData.id && !String(responseData.id).includes('!')) {
        return { ok: true, id: responseData.id, status: responseData.status };
      }
      // If no id field, check if response itself indicates success
      if (typeof responseData === 'string' || typeof responseData === 'number') {
        return { ok: true, id: String(responseData), status: "saved" };
      }
      return { ok: true, id: responseData.id || responseData.Id || "", status: responseData.status || responseData.Status || "saved" };
    }
    return { ok: false };
  } catch (error) {
    console.error("SaveChanges error:", error);
    return { ok: false };
  }
}

/**
 * Posts the complete form submission to the server.
 * @param {FormData} formData - The form data (multipart)
 * @returns {Promise<{ok: boolean, response?: any, errorMessage?: string}>}
 */
export async function postFormToServer(formData) {
  try {
    const response = await fetch(`${REACT_APP_SERVER_URL}docfile`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    if (response.ok) {
      try {
        const data = await response.json();
        return { ok: true, response: data };
      } catch {
        return { ok: true };
      }
    } else {
      const errorResponse = await response.json();
      return { ok: false, errorMessage: errorResponse.Message };
    }
  } catch (error) {
    return { ok: false };
  }
}

/**
 * Records a draft-prompt action for usage statistics. Fire-and-forget: never
 * awaited, never throws, and a failure is invisible to the user.
 * @param {"Used"|"Cancelled"|"DismissedForever"} eventName
 */
export function logDraftPromptEvent(eventName) {
  try {
    fetch(`${REACT_APP_SERVER_URL}LogEvent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventName }),
      credentials: "include",
      keepalive: true, // survives the navigation the hint's profile link triggers
    }).catch(() => { /* statistics are best-effort */ });
  } catch (e) { /* never let logging break the form */ }
}

/**
 * Fetches task data (encrypted txt content) from the server.
 * @param {string} taskId
 * @returns {Promise<{txtData: string, sourceStatus: string, name: string}|false>}
 */
export async function fetchTaskData(taskId) {
  const options = {
    method: 'GET',
    credentials: 'include',
  };

  const apiUrl = `${REACT_APP_SERVER_URL}GetTxt?taskId=${taskId}`;
  const response = await fetch(apiUrl, options);

  if (!response.ok) {
    throw new Error("Failed to fetch the file content");
  }

  return await response.json();
}

/**
 * Fetches and processes the form template.
 * Handles caching in localStorage with obfuscation.
 * @param {string} url - The template API URL
 * @param {string} formId - Form ID for cache key
 * @returns {Promise<object>} The grouped template object
 */
export async function fetchTemplateFromApi(url, formId) {
  const raw = localStorage.getItem(`form_${formId}`);

  if (!raw) {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");

    const values = await response.json();
    const grouped = groupBy3(values, "object");
    const encrypted = EncryptionUtil.obfuscate(grouped);

    localStorage.setItem(`form_${formId}`, encrypted);
    return grouped;
  } else {
    return EncryptionUtil.deobfuscate(raw);
  }
}
